import { moNum } from "@/lib/moNum";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheck, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { isPackSplit, isAutoQty, isPalletLevel, isPerBottle, isAutoC, isAutoLine, isVolC, isPackSplitC, isPalletLevelC, isPalletLine, isTruckLine, basisOfLine, dimOf, qtyInBase, fmtInUnit, UNIT_TO_BASE } from "@/lib/bom";
import { buildOrderBOM, buildAvailability } from "@/lib/orderBom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const cellCls = "h-9 rounded-sm border-slate-300 text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]";
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const num = (n) => (n == null || isNaN(n) ? 0 : Number(n));
const emptyRow = () => ({ _key: uid(), id: null, productId: "", configId: "", casePack: "", quantity: "", unit: "bottles", productionDate: "", designId: "", pricePerCase: "", customerId: "", customerName: "", mwoNumber: "", placed: false, status: "" });
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};
const mapRow = (x) => ({
  _key: uid(), id: x.id,
  productId: x.product_id || "",
  configId: x.config_id || "",
  casePack: x.case_pack != null ? String(x.case_pack) : "",
  quantity: x.quantity != null ? String(x.quantity) : "",
  unit: x.unit || "pallets",
  productionDate: x.production_date || "",
  designId: x.design_id || "",
  pricePerCase: x.price_per_case != null ? String(x.price_per_case) : "",
  customerId: x.customer_id || "",
  customerName: x.customer_name || "",
  mwoNumber: x.mwo_number || "",
  placed: !!x.placed,
  status: x.status || "",
});

export default function MOCreator({ onPlaced, onOpenDetail, onFinished }) {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [components, setComponents] = useState([]);
  const [reservations, setReservations] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [confirm, setConfirm] = useState(null); // placed MO for confirmation modal
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const load = useCallback(async () => {
    try {
      const [o, p, c, rv] = await Promise.all([
        api.get("/manufacturing-orders"),
        api.get("/products"),
        api.get("/components"),
        api.get("/components/reservations"),
      ]);
      setProducts(p.data);
      setComponents(c.data);
      setReservations(rv.data.by_key || {});
      const draft = o.data.filter((x) => !x.placed).map(mapRow);
      setRows(draft.length ? draft : [emptyRow()]);
      api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const compIndex = useMemo(() => {
    const m = {};
    components.forEach((c) => { m[c.id] = c; });
    return m;
  }, [components]);

  const productById = (id) => products.find((p) => p.id === id);
  const rowMultiPack = (row) => (productById(row.productId)?.pack_configs?.length || 0) > 1;
  const rowDesigns = (row) => {
    const p = productById(row.productId);
    if (!p) return [];
    const seen = new Set();
    const out = [];
    (p.lines || []).forEach((l) =>
      (l.designs || []).forEach((d) => { if (d?.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); } })
    );
    return out;
  };
  const rowValid = (row) => {
    const p = productById(row.productId);
    if (!p) return false;
    const designs = rowDesigns(row);
    return !!row.casePack && num(row.quantity) > 0 && !!row.unit && (designs.length === 0 || !!row.designId);
  };
  const fmtVol = (ml) => {
    if (ml >= 1e6) return `${Math.round((ml / 1e6) * 100) / 100} kL`;
    if (ml >= 1000) return `${Math.round((ml / 1000) * 100) / 100} L`;
    return `${Math.round(ml)} mL`;
  };
  const fmtQty = (n) => Math.round(n).toLocaleString();

  // bottles represented by 1 unit of the given kind for this row's product
  const cfgOf = (row) => { const p = productById(row.productId); const cfgs = p?.pack_configs || []; return cfgs.find((c) => c.id === row.configId) || cfgs[0] || {}; };
  const bottlesPerUnit = (row, unit) => {
    const cfg = cfgOf(row);
    const casePackSize = Number(cfg.bottles_per_case) || 0;
    const bpp = casePackSize && cfg.cases_per_pallet ? casePackSize * cfg.cases_per_pallet : 0;
    const ppt = num(cfg.pallets_per_truckload);
    switch (unit) {
      case "bottles": return 1;
      case "cases": return casePackSize;
      case "pallets": return bpp;
      case "truckloads": return bpp * ppt;
      default: return 0;
    }
  };

  const orderMeta = (row) => {
    const p = productById(row.productId);
    const cfg = cfgOf(row);
    const casePackSize = Number(cfg.bottles_per_case) || 0;
    const totalBottles = num(row.quantity) * bottlesPerUnit(row, row.unit);
    const bpp = casePackSize && cfg.cases_per_pallet ? casePackSize * cfg.cases_per_pallet : 0;
    const totalPallets = bpp ? totalBottles / bpp : 0;
    const totalCases = casePackSize ? totalBottles / casePackSize : 0;
    const ppt = num(cfg.pallets_per_truckload);
    const totalTrucks = ppt ? totalPallets / ppt : 0;
    return { p, cfg, casePackSize, totalPallets, totalBottles, totalCases, totalTrucks };
  };

  // convert the displayed quantity when the user switches units, keeping bottles constant
  const onUnitChange = (row, newUnit) => {
    const oldFactor = bottlesPerUnit(row, row.unit);
    const newFactor = bottlesPerUnit(row, newUnit);
    let quantity = row.quantity;
    if (oldFactor > 0 && newFactor > 0 && num(row.quantity) > 0) {
      const bottles = num(row.quantity) * oldFactor;
      quantity = String(Math.round((bottles / newFactor) * 100) / 100);
    }
    update(row._key, { unit: newUnit, quantity }, true);
  };

  const orderBOM = (row) => {
    const { p, cfg, casePackSize, totalPallets, totalCases, totalTrucks } = orderMeta(row);
    if (!p || !casePackSize || num(row.quantity) <= 0) return [];
    return buildOrderBOM(p, components, { cfg, casePackSize, totalCases, totalPallets, totalTrucks, designId: row.designId });
  };

  const availabilityFor = (bom) => buildAvailability(bom, compIndex, reservations);

  const persistRow = async (row) => {
    if (!rowValid(row)) return null;
    setSavingKey(row._key);
    try {
      const payload = {
        product_id: row.productId, case_pack: Number(row.casePack), config_id: row.configId || "",
        quantity: Number(row.quantity), unit: row.unit,
        production_date: row.productionDate, design_id: row.designId || null,
        price_per_case: row.pricePerCase === "" ? null : Number(row.pricePerCase),
        customer_id: row.customerId || "", customer_name: row.customerName || "",
      };
      if (row.id) {
        await api.put(`/manufacturing-orders/${row.id}`, payload);
        return row.id;
      }
      const { data } = await api.post("/manufacturing-orders", payload);
      setRows((prev) => prev.map((r) => (r._key === row._key ? { ...r, id: data.id } : r)));
      return data.id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      return null;
    } finally {
      setSavingKey(null);
    }
  };

  const update = (key, patch, persistNow) => {
    setRows((prev) => {
      const next = prev.map((r) => (r._key === key ? { ...r, ...patch } : r));
      if (persistNow) {
        const row = next.find((r) => r._key === key);
        Promise.resolve().then(() => persistRow(row));
      }
      return next;
    });
  };

  const persistKey = (key) => {
    const row = rowsRef.current.find((r) => r._key === key);
    if (row) persistRow(row);
  };

  const placeOrder = async (row) => {
    if (!rowValid(row)) return;
    setSavingKey(row._key);
    try {
      const id = await persistRow(row);
      if (!id) return;
      const meta = orderMeta(row);
      const bom = orderBOM(row);
      const availability = availabilityFor(bom);
      const { data } = await api.post(`/manufacturing-orders/${id}/place`, {
        bottle_size: meta.p?.name || "",
        ordered_bottles: meta.totalBottles,
        bom_snapshot: bom,
        availability,
      });
      setRows((prev) => {
        const next = prev.filter((r) => r._key !== row._key);
        return next.length ? next : [emptyRow()];
      });
      setConfirm(data);
      api.get("/components/reservations").then((r) => setReservations(r.data.by_key || {})).catch(() => {});
      onPlaced?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSavingKey(null);
    }
  };

  const onProduct = (key, productId) => {
    const p = productById(productId);
    const cfg0 = (p?.pack_configs || [])[0];
    update(key, { productId, designId: "", configId: cfg0?.id || "", casePack: cfg0?.bottles_per_case != null ? String(cfg0.bottles_per_case) : "", pricePerCase: p?.default_price_per_case != null ? String(p.default_price_per_case) : "" }, true);
  };

  const addLine = () =>
    setRows((prev) => [...prev, emptyRow()]);

  const removeRow = async (row) => {
    if (!row.id) { setRows((prev) => prev.filter((r) => r._key !== row._key)); return; }
    setSavingKey(row._key);
    try {
      await api.delete(`/manufacturing-orders/${row.id}`);
      setRows((prev) => prev.filter((r) => r._key !== row._key));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingKey(null); }
  };

  const confAvail = confirm?.availability || [];
  const confShort = confAvail.filter((a) => a.status === "Shortage").length;

  return (
    <section data-testid="manufacturing-orders-page">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Manufacturing Order Creator</h2>
      <p className="mt-1 text-sm text-slate-500">Add a line for each production run and fill in the fields inline.</p>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="max-w-[1340px] border border-slate-200" data-testid="mo-table">
            <div className="hidden grid-cols-[1.4fr_2.2fr_40px] gap-3 border-b border-slate-200 bg-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-700 md:grid">
              <span>Product</span>
              <span>Order BOM</span>
              <span />
            </div>

            {rows.length === 0 && (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="mo-empty">
                <ClipboardCheck className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-slate-500">No orders yet. Add a line to schedule a production run.</p>
              </div>
            )}

            {rows.map((row) => {
              const designs = rowDesigns(row);
              const bom = orderBOM(row);
              const availMap = {};
              availabilityFor(bom).forEach((a) => { availMap[`${a.name}|${a.type}`] = a; });
              return (
                <div key={row._key} className="grid grid-cols-1 gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 md:grid-cols-[1.4fr_2.2fr_40px] md:items-start" data-testid={`mo-row-${row._key}`}>
                  <div className="space-y-1.5">
                    {row.mwoNumber && (
                      <div className="flex items-center justify-between rounded-sm bg-[#002FA7]/5 px-2 py-1 text-[11px] font-semibold text-[#002FA7]" data-testid={`mo-mwo-badge-${row._key}`}>
                        <span>{row.mwoNumber}</span>
                        <span className="font-medium text-slate-500">{row.status}</span>
                      </div>
                    )}
                    <Select value={row.productId || undefined} onValueChange={(v) => onProduct(row._key, v)}>
                      <SelectTrigger className={cellCls} data-testid={`mo-product-select-${row._key}`}>
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-2 gap-1.5">
                      {designs.length > 0 ? (
                        <Select value={row.designId || undefined} onValueChange={(v) => update(row._key, { designId: v }, true)}>
                          <SelectTrigger className={cellCls + " text-slate-600"} data-testid={`mo-design-select-${row._key}`}>
                            <SelectValue placeholder="Select a design" />
                          </SelectTrigger>
                          <SelectContent>
                            {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input readOnly value="" placeholder={row.productId ? "—" : ""} className={cellCls + " bg-slate-50 text-slate-400"} data-testid={`mo-design-none-${row._key}`} />
                      )}
                      {rowMultiPack(row) ? (
                        <Select value={row.configId || undefined} onValueChange={(v) => { const cfg = (productById(row.productId)?.pack_configs || []).find((c) => c.id === v); update(row._key, { configId: v, casePack: cfg?.bottles_per_case != null ? String(cfg.bottles_per_case) : "" }, true); }}>
                          <SelectTrigger className={cellCls} data-testid={`mo-casepack-select-${row._key}`}>
                            <SelectValue placeholder="Select pack" />
                          </SelectTrigger>
                          <SelectContent>
                            {(productById(row.productId)?.pack_configs || []).map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case}-pack`}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input readOnly value={row.casePack ? `${row.casePack}-pack` : ""} placeholder={row.productId ? "—" : ""} className={cellCls + " bg-slate-50 text-slate-600"} data-testid={`mo-casepack-fixed-${row._key}`} />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <Input type="text" inputMode="numeric" value={row.quantity}
                        onChange={(e) => update(row._key, { quantity: e.target.value.replace(/[^0-9.]/g, "") })}
                        onBlur={() => persistKey(row._key)} placeholder="Qty" className={cellCls}
                        data-testid={`mo-quantity-input-${row._key}`} />
                      <Select value={row.unit} onValueChange={(v) => onUnitChange(row, v)}>
                        <SelectTrigger className={cellCls} data-testid={`mo-unit-select-${row._key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bottles">Bottles</SelectItem>
                          <SelectItem value="cases">Cases</SelectItem>
                          <SelectItem value="pallets">Pallets</SelectItem>
                          <SelectItem value="truckloads">Truckloads</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="date" value={row.productionDate}
                      onChange={(e) => update(row._key, { productionDate: e.target.value })}
                      onBlur={() => persistKey(row._key)} className={cellCls}
                      data-testid={`mo-date-input-${row._key}`} />
                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                        <Input type="text" inputMode="decimal" value={row.pricePerCase}
                          onChange={(e) => update(row._key, { pricePerCase: e.target.value.replace(/[^0-9.]/g, "") })}
                          onBlur={() => persistKey(row._key)} placeholder="Price/Case" className={cellCls + " pl-6"} title="Price per case (shown on the Production Order document)"
                          data-testid={`mo-price-input-${row._key}`} />
                      </div>
                      <Select value={row.customerId || undefined} onValueChange={(v) => { const c = customers.find((x) => x.id === v); update(row._key, { customerId: v === "__none" ? "" : v, customerName: v === "__none" ? "" : (c?.company_name || "") }, true); }}>
                        <SelectTrigger className={cellCls + (row.customerId ? "" : " text-slate-400")} data-testid={`mo-customer-select-${row._key}`}>
                          <SelectValue placeholder="Produce for…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">— None —</SelectItem>
                          {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" disabled={!rowValid(row) || savingKey === row._key}
                      onClick={() => placeOrder(row)}
                      className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]"
                      data-testid={`mo-place-order-${row._key}`}>
                      {savingKey === row._key ? <Loader2 className="h-4 w-4 animate-spin" /> : (row.placed ? "Update Order" : "Place Order")}
                    </Button>
                    {row.placed && row.id && (
                      <button type="button" onClick={() => onOpenDetail?.(row.id)}
                        className="flex w-full items-center justify-center gap-1 text-xs text-slate-500 hover:text-[#002FA7]" data-testid={`mo-view-detail-${row._key}`}>
                        <Eye className="h-3.5 w-3.5" /> View Manufacturing Order
                      </button>
                    )}
                  </div>

                  <div className="text-xs" data-testid={`mo-bom-${row._key}`}>
                    {bom.length > 0 ? (
                      <table className="w-full table-fixed border-collapse text-xs">
                        <thead>
                          <tr className="text-[9px] uppercase tracking-[0.08em] text-slate-500">
                            <th className="w-[30%] border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">Component</th>
                            <th className="w-[28%] border border-slate-300 bg-slate-100 px-2 py-1 text-left font-semibold">Type</th>
                            <th className="w-[19%] border border-slate-300 bg-slate-100 px-2 py-1 text-right font-semibold">Qty</th>
                            <th className="w-[23%] border border-slate-300 bg-slate-100 px-2 py-1 text-right font-semibold">Free / Reserved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bom.map((b) => {
                            const a = availMap[`${b.name}|${b.type}`];
                            const short = a && a.status === "Shortage";
                            return (
                            <tr key={`${b.component_id}-${b.type_id}`} className={`align-top ${short ? "bg-red-50" : ""}`} data-testid={`mo-bom-row-${row._key}-${b.component_id}`}>
                              <td className={`border border-slate-300 px-2 py-1 font-semibold ${short ? "text-red-700" : "text-slate-800"}`}>{short && <AlertTriangle className="mr-1 inline h-3 w-3 text-red-600" />}{b.name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-slate-500">{b.type || "—"}</td>
                              <td className={`border border-slate-300 px-2 py-1 text-right font-medium whitespace-nowrap ${short ? "text-red-700" : "text-slate-600"}`}>{b.display}</td>
                              <td className="border border-slate-300 px-2 py-1 text-right whitespace-nowrap"
                                title={a?.reservedOrders?.length ? "Reserved by:\n" + a.reservedOrders.map((o) => `${moNum(o.mwo_number)} — ${o.product_name}${o.design_name ? ` · ${o.design_name}` : ""} (${o.reserved.toLocaleString()})`).join("\n") : undefined}
                                data-testid={`mo-bom-stock-${row._key}-${b.component_id}`}>
                                {a ? (
                                  <>
                                    <div className={`font-semibold ${short ? "text-red-700" : "text-emerald-700"}`}>{a.free.toLocaleString()}{a.unit ? ` ${a.unit}` : ""} free</div>
                                    {a.reserved > 0 && <div className="text-[10px] font-medium text-amber-600">{a.reserved.toLocaleString()}{a.unit ? ` ${a.unit}` : ""} reserved</div>}
                                  </>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <span className="text-slate-400">Fill in the line to see the BOM</span>
                    )}
                  </div>

                  <div className="flex justify-end pt-0.5">
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-600" onClick={() => removeRow(row)} data-testid={`mo-delete-${row._key}`}>
                      {savingKey === row._key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              );
            })}

            <button type="button" onClick={addLine}
              className="flex w-full items-center justify-center gap-2 border-t border-dashed border-slate-300 bg-slate-50/50 px-4 py-3 text-sm font-semibold text-[#002FA7] transition-colors hover:bg-slate-50"
              data-testid="mo-add-line-button">
              <Plus className="h-4 w-4" /> Add line
            </button>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); onFinished?.(); } }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="mo-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading tracking-tight">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> {moNum(confirm?.mwo_number)} Created
            </DialogTitle>
            <DialogDescription className="text-left">Your Manufacturing Work Order has been created and sent to the production queue.</DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="space-y-2 text-sm">
              <div className="font-semibold text-slate-900">{confirm.product_name}</div>
              {confirm.design_name && <div className="text-slate-600">{confirm.design_name}</div>}
              <div className="text-slate-600">{confirm.case_pack}-Pack · {num(confirm.quantity).toLocaleString()} {confirm.unit}</div>
              <div className="text-slate-600">Requested Production Date: {fmtDate(confirm.production_date)}</div>
              <div className="mt-3 rounded-sm border border-slate-200 p-3">
                <div className="flex items-center gap-2 font-medium text-slate-800">
                  Material Status: {confAvail.length - confShort} of {confAvail.length} components available
                </div>
                {confShort > 0 && (
                  <div className="mt-1 flex items-center gap-1.5 text-red-600" data-testid="mo-confirm-shortage">
                    <AlertTriangle className="h-4 w-4" /> {confShort} component shortage detected
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={() => { onOpenDetail?.(confirm.id); setConfirm(null); onFinished?.(); }} data-testid="mo-confirm-view">View Manufacturing Order</Button>
            <Button variant="ghost" className="w-full rounded-sm text-slate-600" onClick={() => { addLine(); setConfirm(null); }} data-testid="mo-confirm-another">Create Another Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </section>
  );
}
